import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Printer, AlertTriangle, CheckCircle2, RefreshCcw } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// POS receipt-print observability. Backing table: `pos_print_logs`
// (migration 0155). Wired 2026-08-17 after cashiers reported cash
// receipts silently failing while UI showed green ✓.
//
// Two panes:
//   1. Summary tiles — success rate by (kind × payment_method) + per-
//      cashier drill-down for the picked window (last 7 / 30 days).
//   2. Recent-failures table — defaults to `outcome != 'ok'` since the
//      operator's job is spotting regressions; toggle to "all" shows
//      every attempt.

interface SummaryRow {
  kind: string
  payment_method: string
  attempts: number
  ok_count: number
  avg_elapsed_ms: number
}

interface CashierRow {
  cashier_id: string
  cashier_name: string | null
  attempts: number
  ok_count: number
}

interface LogRow {
  id: string
  kind: string
  sale_id: string | null
  cashier_id: string | null
  cashier_name: string | null
  store_id: string | null
  payment_method: string | null
  receipt_copies: number | null
  bytes_sent: number | null
  outcome: string
  error_message: string | null
  elapsed_ms: number | null
  created_at: string
  order_number: string | null
}

type WindowChoice = 7 | 30

export default function PrintReliability() {
  const [windowDays, setWindowDays] = useState<WindowChoice>(7)
  const [outcome, setOutcome] = useState<'failures' | 'all'>('failures')

  const { data: summary, refetch: refetchSummary, isFetching: sumFetching } = useQuery({
    queryKey: ['print-logs-summary', windowDays],
    queryFn: () => api.get(`/print-logs/summary?days=${windowDays}`).then(r => r.data as {
      days: number
      totals: { attempts: number; ok_count: number }
      by_kind: SummaryRow[]
      by_cashier: CashierRow[]
    }),
  })

  const { data: logs, refetch: refetchLogs, isFetching: logsFetching } = useQuery({
    queryKey: ['print-logs', outcome],
    queryFn: () => api.get(`/print-logs?outcome=${outcome}&limit=200`).then(r => r.data as { logs: LogRow[] }),
  })

  const successRatePct = useMemo(() => {
    if (!summary || summary.totals.attempts === 0) return null
    return (summary.totals.ok_count / summary.totals.attempts) * 100
  }, [summary])

  const refresh = () => { refetchSummary(); refetchLogs() }

  return (
    <div className="p-6 bg-[#0f0f0f] min-h-screen text-white">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Printer size={22} className="text-[#C41E3A]" />
          <h1 className="text-2xl font-bold">POS Print Reliability</h1>
        </div>
        <div className="flex items-center gap-2">
          {([7, 30] as WindowChoice[]).map(d => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-3 py-1.5 text-xs rounded ${
                windowDays === d
                  ? 'bg-[#C41E3A] text-white font-semibold'
                  : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
              }`}
            >
              Last {d} days
            </button>
          ))}
          <button
            onClick={refresh}
            disabled={sumFetching || logsFetching}
            className="p-1.5 rounded bg-[#1a1a1a] text-gray-400 hover:text-white disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCcw size={14} className={sumFetching || logsFetching ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-6 max-w-3xl">
        Every POS print attempt (receipt, cash-drawer kick, shift-close Z-report, test slip) writes a row to <code className="bg-[#1a1a1a] px-1 rounded">pos_print_logs</code>. Cash-receipt failures were invisible before 2026-08-17; this page turns them into a first-class signal.
      </p>

      {/* Overall + per-kind tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <TileCard
          label="Overall success rate"
          value={successRatePct == null ? '—' : `${successRatePct.toFixed(1)}%`}
          detail={summary ? `${summary.totals.ok_count} / ${summary.totals.attempts} attempts` : ''}
          tone={successRatePct == null ? 'neutral' : successRatePct >= 99 ? 'good' : successRatePct >= 95 ? 'warn' : 'bad'}
        />
        {summary?.by_kind.slice(0, 3).map((r, i) => {
          const rate = r.attempts > 0 ? (r.ok_count / r.attempts) * 100 : null
          const label = r.payment_method
            ? `${r.kind} · ${r.payment_method}`
            : r.kind
          return (
            <TileCard
              key={i}
              label={label}
              value={rate == null ? '—' : `${rate.toFixed(1)}%`}
              detail={`${r.ok_count} / ${r.attempts} · avg ${Math.round(r.avg_elapsed_ms ?? 0)}ms`}
              tone={rate == null ? 'neutral' : rate >= 99 ? 'good' : rate >= 95 ? 'warn' : 'bad'}
            />
          )
        })}
      </div>

      {/* By-kind detail table */}
      <div className="bg-[#161616] rounded-lg border border-[#2a2a2a] mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#2a2a2a]">
          <h2 className="text-sm font-semibold">Success rate by kind + payment method</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#0f0f0f] text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Kind</th>
              <th className="text-left px-4 py-2 font-medium">Payment</th>
              <th className="text-right px-4 py-2 font-medium">Attempts</th>
              <th className="text-right px-4 py-2 font-medium">OK</th>
              <th className="text-right px-4 py-2 font-medium">Rate</th>
              <th className="text-right px-4 py-2 font-medium">Avg ms</th>
            </tr>
          </thead>
          <tbody>
            {(summary?.by_kind ?? []).map((r, i) => {
              const rate = r.attempts > 0 ? (r.ok_count / r.attempts) * 100 : 0
              const rateColor = rate >= 99 ? 'text-emerald-400' : rate >= 95 ? 'text-amber-400' : 'text-[#E8556F]'
              return (
                <tr key={i} className="border-t border-[#2a2a2a]">
                  <td className="px-4 py-2 font-mono text-xs">{r.kind}</td>
                  <td className="px-4 py-2 text-gray-400 text-xs">{r.payment_method || '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.attempts}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-400">{r.ok_count}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${rateColor}`}>
                    {rate.toFixed(1)}%
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {Math.round(r.avg_elapsed_ms ?? 0)}
                  </td>
                </tr>
              )
            })}
            {summary?.by_kind.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500 text-xs">No prints recorded in this window.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Per-cashier table */}
      {summary && summary.by_cashier.length > 0 && (
        <div className="bg-[#161616] rounded-lg border border-[#2a2a2a] mb-6 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#2a2a2a]">
            <h2 className="text-sm font-semibold">Success rate by cashier</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#0f0f0f] text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Cashier</th>
                <th className="text-right px-4 py-2 font-medium">Attempts</th>
                <th className="text-right px-4 py-2 font-medium">OK</th>
                <th className="text-right px-4 py-2 font-medium">Rate</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_cashier.map(r => {
                const rate = r.attempts > 0 ? (r.ok_count / r.attempts) * 100 : 0
                const rateColor = rate >= 99 ? 'text-emerald-400' : rate >= 95 ? 'text-amber-400' : 'text-[#E8556F]'
                return (
                  <tr key={r.cashier_id} className="border-t border-[#2a2a2a]">
                    <td className="px-4 py-2">{r.cashier_name ?? <span className="text-gray-500">Unknown</span>}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.attempts}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-400">{r.ok_count}</td>
                    <td className={`px-4 py-2 text-right tabular-nums font-semibold ${rateColor}`}>
                      {rate.toFixed(1)}%
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent log rows */}
      <div className="bg-[#161616] rounded-lg border border-[#2a2a2a] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#2a2a2a] flex items-center justify-between">
          <h2 className="text-sm font-semibold">Recent print attempts</h2>
          <div className="flex items-center gap-2">
            {(['failures', 'all'] as const).map(o => (
              <button
                key={o}
                onClick={() => setOutcome(o)}
                className={`px-3 py-1 text-xs rounded ${
                  outcome === o
                    ? 'bg-[#C41E3A] text-white font-semibold'
                    : 'bg-[#0f0f0f] text-gray-400 hover:text-white'
                }`}
              >
                {o === 'failures' ? 'Failures only' : 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#0f0f0f] text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Kind</th>
                <th className="text-left px-4 py-2 font-medium">Outcome</th>
                <th className="text-left px-4 py-2 font-medium">Order</th>
                <th className="text-left px-4 py-2 font-medium">Cashier</th>
                <th className="text-left px-4 py-2 font-medium">Payment</th>
                <th className="text-right px-4 py-2 font-medium">Elapsed</th>
                <th className="text-left px-4 py-2 font-medium">Error</th>
              </tr>
            </thead>
            <tbody>
              {(logs?.logs ?? []).map(r => (
                <tr key={r.id} className="border-t border-[#2a2a2a] hover:bg-white/5">
                  <td className="px-4 py-2 text-gray-400 tabular-nums whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                  <td className="px-4 py-2 font-mono">{r.kind}</td>
                  <td className="px-4 py-2">
                    <OutcomeBadge outcome={r.outcome} />
                  </td>
                  <td className="px-4 py-2 font-mono text-gray-400">{r.order_number ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-400">{r.cashier_name ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-400">{r.payment_method ?? '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">{r.elapsed_ms ?? '—'}</td>
                  <td className="px-4 py-2 text-[#E8556F] max-w-md truncate" title={r.error_message ?? ''}>
                    {r.error_message ?? '—'}
                  </td>
                </tr>
              ))}
              {logs?.logs.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-500">No entries.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function TileCard({
  label, value, detail, tone,
}: {
  label: string
  value: string
  detail: string
  tone: 'good' | 'warn' | 'bad' | 'neutral'
}) {
  const border = {
    good: 'border-emerald-800/40',
    warn: 'border-amber-800/40',
    bad: 'border-red-800/40',
    neutral: 'border-[#2a2a2a]',
  }[tone]
  const valueColor = {
    good: 'text-emerald-400',
    warn: 'text-amber-400',
    bad: 'text-[#E8556F]',
    neutral: 'text-white',
  }[tone]
  return (
    <div className={`bg-[#161616] rounded-lg border ${border} p-4`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">{label}</p>
      <p className={`text-2xl font-bold ${valueColor} tabular-nums`}>{value}</p>
      <p className="text-[10px] text-gray-500 mt-1">{detail || ' '}</p>
    </div>
  )
}

function OutcomeBadge({ outcome }: { outcome: string }) {
  const ok = outcome === 'ok'
  const Icon = ok ? CheckCircle2 : AlertTriangle
  const color = ok ? 'text-emerald-400 bg-emerald-950/40' : 'text-[#E8556F] bg-red-950/30'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${color}`}>
      <Icon size={10} />
      {outcome}
    </span>
  )
}
